import { ModuleLesson } from '@yandex-int/k-common/typings';
import b from 'b_';
import { Button, Text } from 'platform-components';
import { scrollToElement } from 'platform-components/utils';
import qs from 'query-string';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useHistory } from 'react-router';
import { useMedia } from 'react-use';
import ym from 'react-yandex-metrika';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { SEARCH_SCROLL_TO_LESSON } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { loadLessonViewData, toggleModal } from 'common.components/lesson-view.store/actions';
import lessonActions from 'common.components/lesson/actions';
import { setWizardVariables } from 'common.components/user/actions';
import { getOpenedCourseContainerSlug } from '../selectors';

import { MEDIA_QUERY_MOBILE } from '../constants';
import * as selectors from '../selectors';

import './course-container__lesson.scss';

interface Props {
    lesson: ModuleLesson;
}

export const CourseContainerLesson = ({ lesson }: Props) => {
    const {
        lesson: { id, name, meta },
        problems_count: cardsCount,
        assigned_count: assignedCount,
    } = lesson;

    const { methodological_goal: methodologicalGoal, time_to_solve: timeToSolve } = meta;

    const isMobile = useMedia(MEDIA_QUERY_MOBILE);
    const ref = useScrollToLessonFromSearchParams(id);
    const openLesson = useOpenLesson(id);
    const wasAssigned = Number(assignedCount) > 0;

    return (
        <div
            className={b('course-container', 'lesson', { assigned: wasAssigned })}
            key={id}
            ref={ref}
            {...accessibleClick(isMobile ? openLesson : undefined)}
        >
            <div className="course-container__lesson-info">
                <div className="course-container__lesson-meta">
                    {methodologicalGoal && (
                        <>
                            <Text
                                className="course-container__lesson-meta-goal"
                                id={`lesson.lessonMeta.methodologicalGoals.${methodologicalGoal}`}
                            />
                        </>
                    )}
                    <Text
                        className="course-container__lesson-meta-cards-count"
                        id="cardsCount"
                        values={{ cardsCount }}
                    />
                    {timeToSolve !== null && (
                        <>
                            <span className="course-container__lesson-meta-separator" />
                            <Text id="lesson.lessonMeta.timeToSolve" values={{ timeToSolve }} />
                        </>
                    )}
                </div>
                <div className="course-container__lesson-name">
                    <Text size={Text.SIZE.L} type={Text.TYPE.MARKDOWN}>
                        {name}
                    </Text>
                </div>
            </div>
            <div className="course-container__lesson-controls">
                {wasAssigned && (
                    <div className="course-container__assigned-info">
                        <Text id="courseContainer.lessonAssignments.count" values={{ assignedCount }} />
                    </div>
                )}
                <Button className="course-container__lesson-button" onClick={openLesson} view={Button.VIEW.PSEUDO}>
                    <Text id="courseContainer.lesson.lookAndAssign" />
                </Button>
            </div>
        </div>
    );
};

export function useOpenLessonCallback() {
    const dispatch = useDispatch();

    const { subject, containerSlug: containerSlugFromUrl } = useParams<{ subject: string; containerSlug: string }>();
    const openedContainerSlug = useSelector(getOpenedCourseContainerSlug);
    const moduleId = useSelector(selectors.getOpenedModule);
    const courseId = useSelector(getCurrentYearCourseId);

    return (lessonId: number, metricParams: { from?: string; subject?: string } = {}) =>
        () => {
            dispatch(lessonActions.clearEditor());
            dispatch(
                loadLessonViewData({
                    courseId,
                    lessonId,
                    moduleId,
                })
            );
            dispatch(toggleModal(true));
            dispatch(setWizardVariables({ courseContainerOpenLessonClicked: true }));

            ym('reachGoal', 'teacher_lesson_creation_readylesson_click', {
                subject: metricParams.subject ?? subject,
                from: metricParams.from,
                lessonId,
                themeId: moduleId,

                slug: containerSlugFromUrl ?? openedContainerSlug,
            });
        };
}

export function useOpenLesson(lessonId: number) {
    return useOpenLessonCallback()(lessonId);
}

function useScrollToLessonFromSearchParams(lessonId: number) {
    const history = useHistory();
    const { search } = useLocation();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const params = qs.parse(search);
        const searchLessonId = Number(params[SEARCH_SCROLL_TO_LESSON]);

        if (searchLessonId === lessonId) {
            scrollToElement(ref.current);

            delete params[SEARCH_SCROLL_TO_LESSON];
            history.replace({ search: qs.stringify(params) });
        }
    }, [lessonId, search, history]);

    return ref;
}
